import { Modal } from "@legal-500/legal500-design-system";
import { useState } from "react";
import { subscriptions } from "../../api/subscriptions.service";
import { FormSuccessScreen } from "../../components/Form/FormSuccessScreen";
import { UnsavedChangesModal } from "../../components/Modal/UnsavedChangesModal";
import type { Subscription } from "../../schemas/subscriptionSchema";
import { SubscriptionForm } from "./SubscriptionForm";

type SubscriptionModalProps = {
  isOpen: boolean;
  onClose: () => void;
  subscription: null;
  mode: "create";
  onSuccess?: () => void;
};

export function SubscriptionModal({ isOpen, onClose, onSuccess }: SubscriptionModalProps) {
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [pendingClose, setPendingClose] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [lastCreatedName, setLastCreatedName] = useState<string | null>(null);

  const handleClose = () => {
    if (hasUnsavedChanges) {
      setPendingClose(true);
    } else {
      resetModal();
    }
  };

  function resetModal() {
    setHasUnsavedChanges(false);
    setPendingClose(false);
    setShowSuccess(false);
    setLastCreatedName(null);
    onClose();
  }

  function confirmDiscardChanges() {
    resetModal();
  }

  const handleSave = async (data: Partial<Subscription>) => {
    setLastCreatedName(`Company ${data.firmId}`);
    await subscriptions.create(data as unknown as Parameters<typeof subscriptions.create>[0]);
    setHasUnsavedChanges(false);
    setShowSuccess(true);
  };

  if (!isOpen) return null;

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title="Create Subscription"
        size="large"
        blurBackground
      >
        {showSuccess ? (
          <FormSuccessScreen
            mode="create"
            itemName={lastCreatedName || "Subscription"}
            singularName="Subscription"
            onClose={() => {
              resetModal();
              onSuccess?.();
            }}
            onCreateAnother={() => {
              setShowSuccess(false);
              setLastCreatedName(null);
              onSuccess?.();
            }}
          />
        ) : (
          <SubscriptionForm
            subscription={null}
            onSave={handleSave}
            onCancel={handleClose}
            onDirtyChange={setHasUnsavedChanges}
          />
        )}
      </Modal>

      {pendingClose && (
        <UnsavedChangesModal
          onClose={() => setPendingClose(false)}
          onConfirm={confirmDiscardChanges}
        />
      )}
    </>
  );
}
